"use client";

import { Login as LoginBase } from "@bigspring/core-components";
import { useEffect, useState } from "react";
import { useSegment } from "@shared/Segment";
import {
  getRuntimeConfigOptional,
  getRuntimeConfig,
} from "@lib/runtimeEnvClient";
import {
  getCompanyProfileId,
  recoverPreviousUrl,
  storeWorkspace,
} from "@utils/auth";
import { parseGraphQLError, parseAxiosError } from "@utils/parseError";
import {
  AuthenticationSource,
  useAuthChannelLazyQuery,
  useAuthInitMutation,
  useAuthInitV2Mutation,
  useAuthVerifyMutation,
  useGoogleAuthVerifyMutation,
  useLearningPlanAssignmentMutation,
  useIsNewAuthLazyQuery,
} from "@gql/generated/graphql";
import { usePostAuthGoogle } from "@rest/generated/auth/oauth/oauth";
import { typewriterTrack } from "@lib/events";
import { ApolloError, useApolloClient } from "@apollo/client";
import { useQueryClient } from "@tanstack/react-query";
import { useCustomRouter } from "@shared/CustomRouter";
import { useRouter } from "next/navigation";
import { FeatureType, AuthErrorCode } from "@utils/constants";
import { AxiosError } from "axios";
import {
  usePostAuthRequestOtp,
  usePostAuthLogin,
} from "@rest/generated/auth/auth/auth";
import { Method } from "../../../typewriter/segment";

export function Login() {
  const { replace: nextReplace } = useRouter();
  const { pathname, replace, query } = useCustomRouter();
  const segment = useSegment();
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();

  const [assignLearningPlan] = useLearningPlanAssignmentMutation();

  const isLpSignup = query.get("lp_token");

  let lp: string;

  let company: string;

  if (isLpSignup) {
    const decoded = atob(isLpSignup as string);
    const params = new URLSearchParams(decoded);
    lp = params.get("lp") as string;
    company = params.get("c") as string;
  }

  const [authInit] = useAuthInitMutation();
  const { mutateAsync: authInitRequest } = usePostAuthRequestOtp();
  const [authInitv2] = useAuthInitV2Mutation();
  const [authVerify] = useAuthVerifyMutation();
  const { mutateAsync: authVerifyRequest } = usePostAuthLogin();
  const [googleAuthVerifyFn] = useGoogleAuthVerifyMutation();
  const { mutateAsync: googlePostAuth } = usePostAuthGoogle();
  const [getIsNewAuth, { data: newAuthData }] = useIsNewAuthLazyQuery();
  const [authChannel] = useAuthChannelLazyQuery();
  const [authInitId, setAuthInitId] = useState("");

  const ssoEnabled =
    getRuntimeConfigOptional("NEXT_PUBLIC_SSO_ENABLED") === "1";

  useEffect(() => {
    void segment.page("Login", { pathname });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signUpVerification = (initId: string) => {
    setAuthInitId(initId);
    localStorage.removeItem("authInitId");
  };

  const onSendVerificationCode = async (profileIdentifier: string) => {
    try {
      const isNewAuthResp = await getIsNewAuth({
        variables: { email: profileIdentifier },
      });
      const isNewAuth = isNewAuthResp.data?.isNewAuth;

      if (isNewAuth) {
        console.log("debug using new auth");
        const results = await authInitRequest({
          data: { profileIdentifier: profileIdentifier },
        }).catch((e) => {
          throw new Error(
            e?.response?.data?.message ||
              e.message ||
              "Unable to init authentication"
          );
        });

        if (!results?.profileIdentifier) {
          throw new Error("Unable to init authentication");
        }

        setAuthInitId(results?.profileIdentifier);
      } else {
        if (isLpSignup) {
          const results = await authInitv2({
            variables: {
              profileIdentifier,
              companyId: company,
            },
          });

          if (!results?.data?.authInitV2) {
            throw new Error("Unable to init authentication");
          }

          setAuthInitId(results.data.authInitV2);
        } else {
          const results = await authInit({
            variables: {
              profileIdentifier,
              workspaceId: getCompanyProfileId() ?? undefined,
            },
          });

          if (!results?.data?.authInit) {
            throw new Error("Unable to init authentication");
          }

          setAuthInitId(results.data.authInit);
        }
      }
    } catch (e) {
      if (e instanceof ApolloError) {
        // @ts-expect-error @typescript-eslint/ban-ts-comment
        throw parseGraphQLError(e.graphQLErrors[0]);
      }

      throw e;
    }
  };

  /**
   * @param verificationCode
   */
  const onSubmitVerificationCode = async (verificationCode: string) => {
    try {
      if (newAuthData?.isNewAuth) {
        sessionStorage.removeItem("sessionId");
        const authData = await authVerifyRequest({
          data: {
            profileIdentifier: authInitId,
            otp: verificationCode,
          },
        }).catch((e) => {
          throw new Error(
            e?.response?.data?.message ||
              e.message ||
              "Unable to init authentication"
          );
        });

        if (!authData) {
          throw {
            code: AuthErrorCode.TOKEN_GENERATION_FAIL,
            message: "Unexpected error while authenticating",
          };
        }

        if (authData?.sessionId) {
          sessionStorage.setItem(
            FeatureType.NEW_AUTH,
            newAuthData?.isNewAuth?.toString()
          );
        }

        if (isLpSignup) {
          await assignLearningPlan({
            variables: {
              profileExternalId: authData?.user?.name,
              companyExternalId: company,
              learningPlanExternalId: lp,
            },
            context: {
              credentials: "omit",
            },
          });
        }

        storeWorkspace(authData?.user?.company_external_id, authData?.user?.id);

        queryClient.clear();

        const previousPath = recoverPreviousUrl();
        if (previousPath) {
          nextReplace(
            `${previousPath.pathname}${previousPath.search}${previousPath.hash}`
          );
        } else {
          await replace("home");
        }
      } else {
        const results = await authVerify({
          variables: {
            authInitId,
            code: verificationCode,
          },
        });

        const authData = results?.data?.authVerify;

        if (!authData) {
          throw {
            code: AuthErrorCode.TOKEN_GENERATION_FAIL,
            message: "Unexpected error while authenticating",
          };
        }

        if (authData.mfaRequired) {
          return { mfaRequired: true };
        }

        // assign LP to user
        if (isLpSignup) {
          await assignLearningPlan({
            variables: {
              profileExternalId: authData.profileExternalId,
              companyExternalId: company,
              learningPlanExternalId: lp,
            },
            context: {
              credentials: "omit",
            },
          });
        }
        //

        storeWorkspace(authData.companyExternalId, authData.companyProfileId);

        void typewriterTrack("userLoggedIn", {
          method: Method.Email,
          companyExternalId: authData.companyExternalId,
          profileExternalId: authData.profileExternalId,
        });

        await apolloClient.resetStore();

        const previousPath = recoverPreviousUrl();
        if (previousPath) {
          nextReplace(
            `${previousPath.pathname}${previousPath.search}${previousPath.hash}`
          );
        } else {
          await replace("home");
        }
      }
    } catch (e) {
      if (e instanceof ApolloError) {
        // @ts-expect-error @typescript-eslint/ban-ts-comment
        throw parseGraphQLError(e.graphQLErrors[0]);
      }

      if (e instanceof AxiosError) {
        throw parseAxiosError(e);
      }

      throw e;
    }
  };

  /**
   *
   * @param profileId
   */
  const onSsoSignin = async (profileId: string) => {
    const isNewAuthResp = await getIsNewAuth({
      variables: { email: profileId },
    });
    const isNewAuth = isNewAuthResp.data?.isNewAuth;

    if (isNewAuth) {
      sessionStorage.removeItem("sessionId");
      const profileIdEmail = encodeURIComponent(profileId);
      const authDomainUrl = getRuntimeConfig("NEXT_PUBLIC_AUTH_DOMAIN_URL");

      window.location.href = `${authDomainUrl}/sso/init/PWA?email=${profileIdEmail}`;
    } else {
      try {
        const { data } = await authChannel({
          variables: { profileIdentifier: profileId },
        });

        const chType = data?.authChannel?.channelType;
        const companyExtId = data?.authChannel?.companyExtId;
        const companyProfileId = data?.authChannel?.companyProfileId;

        if (!chType || chType !== "SSO") {
          throw new Error(
            "SSO is not enabled for this account. Please request a login code."
          );
        }

        if (!companyExtId || !companyProfileId) {
          throw new Error("Company profile not found for this email");
        }

        const base = data?.authChannel?.ssoChannel?.idpProxyBaseUrl;
        const path = data?.authChannel?.ssoChannel?.idpProxyPath;
        const url = new URL(`${base}${path}`);
        url.searchParams.append("username", profileId);

        typewriterTrack("userLoggedIn", {
          method: Method.Sso,
          companyExternalId: companyExtId,
          profileExternalId: profileId,
        });

        storeWorkspace(companyExtId, companyProfileId);

        nextReplace(url.toString());
      } catch (e) {
        if (e instanceof ApolloError) {
          // @ts-expect-error @typescript-eslint/ban-ts-comment
          throw parseGraphQLError(e.graphQLErrors[0]);
        }

        throw e;
      }
    }
  };

  const onGAuthSuccess = async (credential: string) => {
    let authEmail: string | undefined = undefined;
    try {
      const parts = credential.split(".");
      if (parts.length !== 3)
        throw new Error("Invalid Google Auth JWT format !");
      const payload = JSON.parse(atob(parts[1]));
      authEmail = payload?.email as string;

      const isNewAuthResp = await getIsNewAuth({
        variables: { email: authEmail },
      });
      const isNewAuth = isNewAuthResp.data?.isNewAuth;

      if (isNewAuth) {
        console.log("debug using new auth");
        sessionStorage.removeItem("sessionId");
        const gauthResponse = await googlePostAuth({
          data: { credential: credential },
        }).catch((e) => {
          throw new Error(
            e?.response?.data?.message ||
              e.message ||
              "Unable to init authentication"
          );
        });

        if (gauthResponse?.sessionId) {
          sessionStorage.setItem(FeatureType.NEW_AUTH, isNewAuth?.toString());
        }

        // const authData = gauthResponse?.data?.googleAuthVerify;
        if (
          !gauthResponse?.tokens?.access?.token ||
          !gauthResponse?.tokens?.refresh?.token
        ) {
          throw {
            code: AuthErrorCode.TOKEN_GENERATION_FAIL,
            message: "Unexpected error while authenticating",
          };
        }

        await apolloClient.resetStore();

        storeWorkspace(
          gauthResponse?.user?.company_external_id,
          gauthResponse?.user?.name
        );
      } else {
        const gauthResponse = await googleAuthVerifyFn({
          variables: {
            idToken: credential,
            source: AuthenticationSource.App,
          },
        });

        const authData = gauthResponse?.data?.googleAuthVerify;
        if (!authData?.accessToken || !authData?.refreshToken) {
          throw {
            code: AuthErrorCode.TOKEN_GENERATION_FAIL,
            message: "Unexpected error while authenticating",
          };
        }

        storeWorkspace(authData.companyExternalId, authData.companyProfileId);

        const photoUpdateStatus =
          gauthResponse?.data?.googleAuthVerify.socialPhotoUpdateStatus;
        const photoUpdateErr =
          gauthResponse?.data?.googleAuthVerify.socialPhotoUpdateErrorMessage ??
          "";

        if (photoUpdateStatus === "success") {
          typewriterTrack("socialPhotoUpdated", {
            email: authEmail,
            source: "pwa",
          });
        } else if (photoUpdateStatus === "error") {
          typewriterTrack("socialPhotoUpdationFailed", {
            email: authEmail,
            source: "pwa",
            cause: photoUpdateErr,
          });
        }
      }

      const previousPath = recoverPreviousUrl();
      if (previousPath) {
        nextReplace(
          `${previousPath.pathname}${previousPath.search}${previousPath.hash}`
        );
      } else {
        await replace("home");
      }
    } catch (e) {
      typewriterTrack("googleSigninAuthVerifyFailure", {
        email: authEmail,
        source: "pwa",
        cause: (e as Error).message,
      });

      if (e instanceof ApolloError) {
        // @ts-expect-error @typescript-eslint/ban-ts-comment
        throw parseGraphQLError(e.graphQLErrors[0]);
      }

      throw e;
    }
  };

  return (
    <LoginBase
      source="PWA"
      signUpVerification={signUpVerification}
      onSendVerificationCode={onSendVerificationCode}
      onSubmitVerificationCode={onSubmitVerificationCode}
      isSsoEnabled={ssoEnabled}
      onSsoLogin={onSsoSignin}
      onGoogleAuthSuccess={onGAuthSuccess}
      segment={segment}
      googleApiKey={getRuntimeConfig("NEXT_PUBLIC_GOOGLE_API_KEY")}
      googleMapsApiKey={getRuntimeConfig("NEXT_PUBLIC_GOOGLE_MAPS_API_KEY")}
    />
  );
}
